import React, { useState } from 'react';
import { GraphView } from '../RecipeGraph';
import { IngredientPanel } from '../RecipeView';
import { saveRecipe } from '../../api/actions';

export default function RecipePreview({ recipe, onSaved, onNavigateToBrowser }) {
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [showSaveInput, setShowSaveInput] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('steps');

    const handleSaveClick = () => {
        setSaveName(recipe?.name || '');
        setShowSaveInput(true);
        setError(null);
    };

    const handleSave = async () => {
        if (!saveName.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const savedRecipe = await saveRecipe(recipe, saveName.trim());
            setSaved(true);
            setShowSaveInput(false);
            if (onSaved) onSaved(savedRecipe);
        } catch (err) {
            setError(err.message || 'Failed to save recipe');
        } finally {
            setSaving(false);
        }
    };

    if (!recipe) {
        return (
            <div className="recipe-preview-empty">
                <p>Your recipe will appear here as a DAG visualization once you paste or describe a recipe in the chat.</p>
            </div>
        );
    }

    return (
        <div className="recipe-preview">
            <div className="recipe-preview-header">
                <h3>{recipe.name}</h3>
                <div className="recipe-preview-tabs">
                    <button
                        className={`recipe-tab ${activeTab === 'ingredients' ? 'active' : ''}`}
                        onClick={() => setActiveTab('ingredients')}
                    >
                        Ingredients
                    </button>
                    <button
                        className={`recipe-tab ${activeTab === 'steps' ? 'active' : ''}`}
                        onClick={() => setActiveTab('steps')}
                    >
                        Steps
                    </button>
                </div>
                <div className="recipe-preview-actions">
                    {saved ? (
                        <>
                            <span className="save-success">Saved!</span>
                            {onNavigateToBrowser && (
                                <button
                                    className="nav-to-browser-button"
                                    onClick={onNavigateToBrowser}
                                >
                                    Go to Browse
                                </button>
                            )}
                        </>
                    ) : showSaveInput ? (
                        <div className="save-input-group">
                            <input
                                type="text"
                                value={saveName}
                                onChange={e => setSaveName(e.target.value)}
                                placeholder="Recipe name"
                                className="save-name-input"
                                onKeyDown={e => e.key === 'Enter' && handleSave()}
                            />
                            <button
                                className="save-confirm-button"
                                onClick={handleSave}
                                disabled={saving || !saveName.trim()}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                className="save-cancel-button"
                                onClick={() => setShowSaveInput(false)}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button className="save-recipe-button" onClick={handleSaveClick}>
                            Save Recipe
                        </button>
                    )}
                </div>
            </div>
            {error && <div className="preview-error">{error}</div>}
            <div className="recipe-preview-body">
                {activeTab === 'ingredients' ? (
                    <IngredientPanel recipe={recipe} />
                ) : (
                    <div className="recipe-preview-graph">
                        <GraphView recipe={recipe} />
                    </div>
                )}
            </div>
        </div>
    );
}
